'use client';

import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { styled, css } from 'styled-components';
import cs from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Icon } from '@wistia/ui';
import { BREAKPOINT_DRAWER } from '@content/navigation/navData';
import { trackLink } from '@lib/tracking';
import FeaturedMedia from '@content/navigation/simplified/featured-media';
import MochinePromo from '@content/navigation/simplified/mochine-promo';
import type { NavMenu } from '@content/navigation/simplified/nav-data';
import {
  copyStyles,
  Description,
  menuAccents,
} from '@content/navigation/simplified/styles';

// The panel's drop shadow color: mostly transparent, not mixed with the page.
const panelShadowColor = (color: string) =>
  `color-mix(in srgb, ${color} 12%, transparent)`;

const MenuItem = styled.li`
  position: relative;
  ${menuAccents}
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    display: flex;
  }
`;

const triggerStyles = css`
  align-items: center;
  color: ${({ theme }) => theme.colors.grey800};
  display: flex;
  font-family: var(--font-walsheim);
  font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
  font-weight: ${({ theme }) => theme.typography.fontWeight.link};
  line-height: ${({ theme }) => theme.typography.lineHeight.headingFour};
  padding-block: ${({ theme }) => theme.spacing[0.75]};
  width: 100%;
  &:hover,
  &:focus-visible,
  &.isOpen {
    color: var(--nav-accent);
  }
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    display: inline-flex;

    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
    height: 100%;
    line-height: ${({ theme }) => theme.typography.lineHeight.body};
    padding-block: 0;
    padding-inline: ${({ theme }) => theme.spacing[0.75]};
    transition: color var(--nav-label-duration) var(--nav-label-ease);
    white-space: nowrap;

    &:hover,
    &:focus-visible,
    &.isOpen {
      color: var(--nav-accent);
    }
  }
`;

const Trigger = styled(Link)`
  ${triggerStyles}
`;

const Disclosure = styled.details`
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    height: 100%;
  }
`;

const Summary = styled.summary`
  ${triggerStyles}
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
`;

const Caret = styled(Icon)`
  color: currentcolor;

  margin-inline-start: 0.375rem;
  transition: transform var(--nav-caret-duration) var(--nav-caret-ease);

  .isOpen & {
    transform: rotate(180deg);
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    flex-direction: row;
    background: ${({ theme }) => theme.colors.blue100};
    border-radius: 0 0 ${({ theme }) => theme.radius.mini}
      ${({ theme }) => theme.radius.mini};
    /* Same hairline as the nav bar, on every side, so the panel reads as its
       own surface over a blue100 page section, plus elevation2's geometry
       in a mostly transparent blue800 (no page-color mix) for subtle depth. */
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => theme.colors.blue200},
      0 2px 12px 0 ${({ theme }) => panelShadowColor(theme.colors.blue800)};
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 100%;
    transform: translateY(-${({ theme }) => theme.spacing[0.5]});
    transition:
      transform var(--nav-panel-duration) var(--nav-ease),
      opacity var(--nav-panel-duration) var(--nav-ease),
      visibility 0s linear var(--nav-panel-duration);
    visibility: hidden;
    width: max-content;

    &.isOpen {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
      transition-delay: 0s;
      visibility: visible;
    }
  }
`;

const Links = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[0.75]} 0
    ${({ theme }) => theme.spacing[1.5]};
  gap: ${({ theme }) => theme.spacing[0.75]};
  > li.overview {
    display: none;
  }
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    padding: ${({ theme }) => theme.spacing[0.75]}
      ${({ theme }) => theme.spacing[0.25]} ${({ theme }) => theme.spacing[1]}
      ${({ theme }) => theme.spacing[1]};
    row-gap: ${({ theme }) => theme.spacing[0.5]};
    width: 14rem;

    > li.overview {
      display: list-item;
    }
  }
`;

const Row = styled(Link)`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.blue700};
  display: flex;
  gap: ${({ theme }) => theme.spacing[0.75]};
  padding-block: ${({ theme }) => theme.spacing[0.25]};
  &:hover,
  &:focus-visible {
    color: var(--nav-accent);
  }
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    align-items: center;
    border-radius: ${({ theme }) => theme.radius.mini};
    gap: 0.625rem;
    padding-block: ${({ theme }) => theme.spacing[0.5]};
    padding-inline: ${({ theme }) => theme.spacing[0.75]};
    transition:
      color var(--nav-row-duration) var(--nav-ease),
      background-color var(--nav-row-duration) var(--nav-ease),
      transform var(--nav-row-duration) var(--nav-ease);

    &:hover,
    &:focus-visible {
      background-color: ${({ theme }) => theme.colors.white};
      color: var(--nav-accent);
    }

    &.overview {
      background-color: ${({ theme }) => theme.colors.blue200};
      color: ${({ theme }) => theme.colors.blue800};
      margin-block-end: ${({ theme }) => theme.spacing[0.25]};

      &:hover,
      &:focus-visible {
        background-color: ${({ theme }) => theme.colors.blue500};
        color: ${({ theme }) => theme.colors.white};
      }
    }

    @media (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference) {
      &:hover,
      &:focus-visible {
        transform: translateY(-1px);
      }
    }
  }
`;

const RowIcon = styled(Icon)`
  color: currentcolor;
  flex: 0 0 auto;
`;

const RowSvg = styled.svg`
  display: block;
  flex: 0 0 auto;
  height: 16px;
  width: 16px;
`;

const RowCopy = styled.span`
  ${copyStyles}
  font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
  line-height: ${({ theme }) => theme.typography.lineHeight.headingFour};
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    ${copyStyles}
  }
`;

const Aside = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 20rem;
  padding-block-end: ${({ theme }) => theme.spacing[1.5]};
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    margin-block: ${({ theme }) => theme.spacing[0.75]}
      ${({ theme }) => theme.spacing[1]};
    padding-block-end: 0;
    padding-inline: ${({ theme }) => theme.spacing[0.25]}
      ${({ theme }) => theme.spacing[1]};
    width: 17.5rem;
  }
`;

const Featured = styled(Link)`
  color: ${({ theme }) => theme.colors.blue700};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[0.75]};
  &:hover,
  &:focus-visible {
    color: var(--nav-accent);
  }
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    border-radius: ${({ theme }) => theme.radius.mini};
    gap: ${({ theme }) => theme.spacing[0.75]};
    margin-block-start: ${({ theme }) => theme.spacing[0.25]};
    padding: ${({ theme }) => theme.spacing[0.5]};
    transition:
      color var(--nav-row-duration) var(--nav-ease),
      background-color var(--nav-row-duration) var(--nav-ease),
      transform var(--nav-row-duration) var(--nav-ease);

    &:hover,
    &:focus-visible {
      background-color: ${({ theme }) => theme.colors.white};
      color: var(--nav-accent);

      --nav-description-color: ${({ theme }) => theme.colors.grey700};
    }

    @media (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference) {
      &:hover,
      &:focus-visible {
        transform: translateY(-1px);
      }
    }
  }
`;

const MenuPromo = styled(MochinePromo)`
  margin-block-start: ${({ theme }) => theme.spacing[0.75]};
  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    margin: ${({ theme }) => theme.spacing[0.75]}
      ${({ theme }) => theme.spacing[0.5]} ${({ theme }) => theme.spacing[0.25]};
  }
`;

const OverviewIcon = () => (
  <RowSvg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path
      d="M12 3.5 21 8.25 12 13 3 8.25Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M3 12.25 12 17l9-4.75M3 16.25 12 21l9-4.75"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </RowSvg>
);

const Menu = ({
  menu,
  onMenuClick,
}: {
  menu: NavMenu;
  onMenuClick: () => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Touch toggles the menu, so a touch anywhere outside it closes it.
  const ref = useRef<HTMLLIElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const disclosureRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handler = (event: TouchEvent) => {
      if (ref.current?.contains(event.target as Node) === false) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('touchstart', handler);
    return () => document.removeEventListener('touchstart', handler);
  }, [isMenuOpen]);

  const router = useRouter();
  const isHoverDesktop = () =>
    window.matchMedia(
      `(min-width: ${BREAKPOINT_DRAWER}) and (hover: hover) and (pointer: fine)`,
    ).matches;
  const openMenu = () => {
    if (isHoverDesktop()) setIsMenuOpen(true);
  };
  const closeMenu = () => {
    if (disclosureRef.current) disclosureRef.current.open = false;
    setIsMenuOpen(false);
    setIsPreviewing(false);
  };
  const handleClose = () => {
    closeMenu();
    onMenuClick();
  };
  const handleLinkClick = (trackingLabel: string, trackingProperty: string) => {
    trackLink(trackingLabel, trackingProperty);
    handleClose();
  };
  // On hover desktops the menu opens on hover, so a mouse click on the label
  // goes to the section page. Keyboard (detail 0) and touch still toggle.
  const handleSummaryClick = (event: MouseEvent<HTMLElement>) => {
    if (event.detail === 0 || !isHoverDesktop()) return;
    event.preventDefault();
    trackLink(menu.trackingLabel, menu.trackingProperty);
    if (event.metaKey || event.ctrlKey) {
      window.open(menu.href, '_blank', 'noopener');
      return;
    }
    handleClose();
    router.push(menu.href);
  };

  if (!menu.links) {
    return (
      <MenuItem className={menu.id}>
        <Trigger
          href={menu.href}
          onClick={() =>
            handleLinkClick(menu.trackingLabel, menu.trackingProperty)
          }
        >
          {menu.label}
        </Trigger>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      ref={ref}
      className={menu.id}
      onMouseEnter={openMenu}
      onMouseLeave={() => {
        if (!ref.current?.contains(document.activeElement)) closeMenu();
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
      }}
    >
      <Disclosure
        ref={disclosureRef}
        open={isMenuOpen}
        onToggle={(event) => setIsMenuOpen(event.currentTarget.open)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            closeMenu();
            summaryRef.current?.focus();
          }
        }}
      >
        <Summary
          ref={summaryRef}
          className={cs({ isOpen: isMenuOpen })}
          onClick={handleSummaryClick}
        >
          {menu.label}
          <Caret aria-hidden="true" role="img" size="sm" type="caret-down" />
        </Summary>
        <Panel className={cs({ isOpen: isMenuOpen })}>
          <Links role="list">
            {menu.overview ? (
              <li className="overview">
                <Row
                  className="overview"
                  href={menu.overview.href}
                  onClick={() =>
                    handleLinkClick(
                      menu.overview.trackingLabel,
                      menu.overview.trackingProperty,
                    )
                  }
                >
                  <OverviewIcon />
                  <RowCopy>{menu.overview.name}</RowCopy>
                </Row>
              </li>
            ) : null}
            {menu.links.map((item) => (
              <li key={item.name}>
                <Row
                  href={item.href}
                  onClick={() =>
                    handleLinkClick(item.trackingLabel, item.trackingProperty)
                  }
                >
                  <RowIcon
                    aria-hidden="true"
                    role="img"
                    size="md"
                    type={item.icon}
                  />
                  <RowCopy>{item.name}</RowCopy>
                </Row>
              </li>
            ))}
          </Links>
          {menu.featured ? (
            <Aside>
              <Featured
                href={menu.featured.href}
                onBlur={() => setIsPreviewing(false)}
                onClick={() =>
                  handleLinkClick(
                    menu.featured.trackingLabel,
                    menu.featured.trackingProperty,
                  )
                }
                onFocus={() => setIsPreviewing(true)}
                onMouseEnter={() => setIsPreviewing(true)}
                onMouseLeave={() => setIsPreviewing(false)}
              >
                <FeaturedMedia
                  featured={menu.featured}
                  isPlaying={isPreviewing}
                />
                <RowCopy>
                  {menu.featured.name}
                  <Description>{menu.featured.description}</Description>
                </RowCopy>
              </Featured>
              {menu.promo ? (
                <MenuPromo onClick={handleClose} promo={menu.promo} />
              ) : null}
            </Aside>
          ) : null}
        </Panel>
      </Disclosure>
    </MenuItem>
  );
};

export default Menu;
